/**
 * TEMP-H-005: Type Guards & Utilities
 * 
 * Type-safe helpers for stage names, event types, and runtime type checking.
 * Provides exhaustive switch checks and narrowing utilities.
 */

import { 
  StageName, 
  Stage, 
  ExecutionStatusType,
  ExecutionStatus,
  EventTypeName,
  EventType,
  PipelineEvent,
  ContentReadyEvent,
  ScoresReadyEvent,
  StageStartedEvent,
  StageCompletedEvent,
  StageFailedEvent,
  BillingEvent,
  QCDecisionType,
  QCDecision
} from './shared-types';

// =============================================================================
// STAGE UTILITIES
// =============================================================================

/**
 * All valid stage names as readonly array
 */
export const STAGE_NAMES = Stage.options as readonly StageName[];

/**
 * Check if a string is a valid stage name
 */
export function isValidStage(value: unknown): value is StageName {
  return Stage.safeParse(value).success;
}

/**
 * Normalize legacy stage names to canonical form
 */
export function normalizeStage(stage: string): StageName {
  // Handle legacy 'video' -> 'videos' conversion
  if (stage === 'video') {
    return 'videos';
  }
  
  if (isValidStage(stage)) {
    return stage;
  }
  
  throw new Error(`Invalid stage name: ${stage}. Valid stages: ${STAGE_NAMES.join(', ')}`);
}

/**
 * Get human-readable stage display name
 */
export function getStageDisplayName(stage: StageName): string {
  const displayNames: Record<StageName, string> = {
    script: 'Script',
    vision: 'Vision',
    storyboard: 'Storyboard', 
    shots: 'Shots',
    continuity: 'Continuity',
    elements: 'Elements',
    frames: 'Frames',
    videos: 'Videos',
    qc: 'Quality Control',
    assemble: 'Assembly',
    music: 'Music',
    music_master: 'Music Master',
    music_sidechain: 'Music Sidechain',
    export_preview: 'Export Preview',
    publish: 'Publishing'
  };
  
  return displayNames[stage] || stage;
}

/**
 * Check if stage supports specific features
 */
export function stageSupportsFeature(stage: StageName, feature: 'qc' | 'artifacts' | 'personas'): boolean {
  const featureMap: Record<string, StageName[]> = {
    qc: ['script', 'vision', 'storyboard', 'shots', 'continuity', 'elements', 'frames', 'videos', 'music'],
    artifacts: ['storyboard', 'shots', 'frames', 'videos', 'music', 'assemble'], 
    personas: ['script', 'vision', 'storyboard', 'shots', 'continuity', 'elements', 'frames', 'videos', 'music', 'qc']
  };
  
  return featureMap[feature]?.includes(stage) ?? false;
}

// =============================================================================
// EXECUTION STATUS UTILITIES  
// =============================================================================

/**
 * All valid execution status values
 */
export const EXECUTION_STATUSES = ExecutionStatus.options as readonly ExecutionStatusType[];

/**
 * Check if a string is a valid execution status
 */
export function isValidExecutionStatus(value: unknown): value is ExecutionStatusType {
  return ExecutionStatus.safeParse(value).success;
}

/**
 * Get human-readable status display name
 */
export function getExecutionStatusDisplayName(status: ExecutionStatusType): string {
  const displayNames: Record<ExecutionStatusType, string> = {
    queued: 'Queued',
    running: 'Running',
    paused: 'Paused', 
    completed: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled'
  };
  
  return displayNames[status];
}

/**
 * Check if execution status allows modifications
 */
export function canModifyExecution(status: ExecutionStatusType): boolean {
  return ['queued', 'running', 'paused'].includes(status);
}

// =============================================================================  
// EVENT TYPE UTILITIES
// =============================================================================

/**
 * All valid event types
 */
export const EVENT_TYPES = EventType.options as readonly EventTypeName[];

/**
 * Check if a string is a valid event type
 */
export function isValidEventType(value: unknown): value is EventTypeName {
  return EventType.safeParse(value).success;
}

/**
 * Get human-readable event type display name  
 */
export function getEventTypeDisplayName(eventType: EventTypeName): string {
  const displayNames: Record<EventTypeName, string> = {
    content_ready: 'Content Ready',
    scores_ready: 'Scores Ready',
    stage_started: 'Stage Started', 
    stage_completed: 'Stage Completed',
    stage_failed: 'Stage Failed',
    billing_committed: 'Billing Committed'
  };
  
  return displayNames[eventType];
}

// =============================================================================
// EVENT TYPE GUARDS & NARROWING
// =============================================================================

/**
 * Type guard for content ready events
 */
export function isContentReadyEvent(event: PipelineEvent): event is ContentReadyEvent {
  return event.event_type === 'content_ready';
}

/**
 * Type guard for scores ready events
 */  
export function isScoresReadyEvent(event: PipelineEvent): event is ScoresReadyEvent {
  return event.event_type === 'scores_ready';
}

/**
 * Type guard for stage started events
 */
export function isStageStartedEvent(event: PipelineEvent): event is StageStartedEvent {
  return event.event_type === 'stage_started';
}

/**
 * Type guard for stage completed events
 */
export function isStageCompletedEvent(event: PipelineEvent): event is StageCompletedEvent {
  return event.event_type === 'stage_completed';  
}

/**
 * Type guard for stage failed events
 */
export function isStageFailedEvent(event: PipelineEvent): event is StageFailedEvent {
  return event.event_type === 'stage_failed';
}

/**
 * Type guard for billing events
 */
export function isBillingEvent(event: PipelineEvent): event is BillingEvent {
  return event.event_type === 'billing_committed';
}

/**
 * Narrow event to specific type - throws if wrong type
 */
export function narrowEvent<T extends EventTypeName>(
  event: PipelineEvent,
  expectedType: T
): Extract<PipelineEvent, { event_type: T }> {
  if (event.event_type !== expectedType) {
    throw new Error(`Expected event type ${expectedType}, got ${event.event_type}`);
  }
  
  return event as Extract<PipelineEvent, { event_type: T }>;
}

// =============================================================================
// QC DECISION UTILITIES
// =============================================================================

/**
 * All valid QC decision values
 */
export const QC_DECISIONS = QCDecision.options as readonly QCDecisionType[];

/**
 * Check if a string is a valid QC decision
 */
export function isValidQCDecision(value: unknown): value is QCDecisionType {
  return QCDecision.safeParse(value).success;
}

/**
 * Get human-readable QC decision display name
 */
export function getQCDecisionDisplayName(decision: QCDecisionType): string {
  const displayNames: Record<QCDecisionType, string> = {
    pass: 'Pass',
    fail: 'Fail', 
    retry: 'Retry',
    escalate: 'Escalate'
  };
  
  return displayNames[decision];
}

// =============================================================================
// EXHAUSTIVE SWITCH UTILITIES
// =============================================================================

/**
 * Ensures exhaustive handling in switch statements
 * Use in default case to catch missing cases at compile time
 */
export function assertNever(value: never): never {
  throw new Error(`Unhandled case: ${JSON.stringify(value)}`);
}

/**
 * Generic exhaustive checker with descriptive error
 */
export function assertExhaustive<T extends string | number>(
  value: T,
  context: string
): never {
  throw new Error(`Unhandled ${context}: ${value}`);
}

/**
 * Exhaustive event type handler - ensures all event types are covered
 */
export function handleEventExhaustively(event: PipelineEvent): void {
  switch (event.event_type) {
    case 'content_ready':
      // Handle content ready event
      break;
    case 'scores_ready':
      // Handle scores ready event  
      break;
    case 'stage_started':
      // Handle stage started event
      break;
    case 'stage_completed':
      // Handle stage completed event
      break;
    case 'stage_failed':
      // Handle stage failed event
      break;
    case 'billing_committed':
      // Handle billing event
      break;
    default:
      assertNever(event as never);
  }
}

/**
 * Exhaustive stage handler - ensures all stages are covered
 */
export function handleStageExhaustively(stage: StageName): void {
  switch (stage) {
    case 'script':
    case 'vision': 
    case 'storyboard':
    case 'shots':
    case 'continuity':
    case 'elements':
    case 'frames': 
    case 'videos':
    case 'qc':
    case 'assemble':
    case 'music':
    case 'music_master':
    case 'music_sidechain':
    case 'export_preview':
    case 'publish':
      // Handle each stage
      break;
    default:
      assertNever(stage as never);
  }
}